import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity, View } from 'react-native';

import { IconPlayerButton } from './icon-audio-recorder-button/icon-recorder-button';
import styles from './icon-audio-recorder.style';
import { useColors } from '../../hooks';
import { useRecord } from '../../hooks/use-record';
import { useSoundContext } from '../../hooks/use-sound-context';
import type { ColorsType } from '../../interfaces/colors.type';
import type { FontSizeType } from '../../interfaces/fonts.type';
import ThemedText2 from '../themed-text-2';

interface IconAudioRecorderProps {
  index: number;
  isRecorded: () => void;
  fontSize?: FontSizeType;
  color?: keyof ColorsType;
}

export function IconAudioRecorder({
  index,
  color = 'onPrimary',
  fontSize = 'medium',
  isRecorded,
}: IconAudioRecorderProps) {
  const colors = useColors();
  const { t } = useTranslation();
  const {
    record,
    stop,
    play,
    isRecording,
    recorded,
    setRecorded,
    isPermissionDenied,
  } = useRecord();

  const { pause } = useSoundContext();

  useEffect(() => {
    if (isRecorded && recorded) {
      isRecorded();
    }
  }, [recorded]);

  useEffect(() => {
    if (isRecording) {
      pause();
    }
  }, [isRecording, pause]);

  useEffect(() => {
    setRecorded(false);
  }, [index, setRecorded]);

  const togglePlay = () => {
    setRecorded(false);
  };

  return (
    <View style={[styles.view]}>
      {!recorded && !isRecording && (
        <View style={styles.recordContainer}>
          <TouchableOpacity
            onPress={record}
            style={[
              styles.container,
              {
                backgroundColor: isPermissionDenied
                  ? colors.gray
                  : colors.primary,
              },
            ]}
          >
            <IconPlayerButton
              colors={color}
              size={fontSize}
              iconName="microphone"
            />
          </TouchableOpacity>
          {isPermissionDenied && (
            <View style={styles.errorContainer}>
              <ThemedText2 fontSize={18} type="error" fontWeightStyle="bold">
                {t('permission.microphone.message')}
              </ThemedText2>
              <ThemedText2 type="error" style={styles.errorDescription}>
                {t('permission.microphone.description.mobile')}
              </ThemedText2>
            </View>
          )}
        </View>
      )}
      {isRecording && (
        <TouchableOpacity
          onPress={stop}
          style={[styles.container, { backgroundColor: colors.primary }]}
        >
          <IconPlayerButton colors={color} size={fontSize} iconName="square" />
        </TouchableOpacity>
      )}
      {recorded && (
        <>
          <TouchableOpacity
            onPress={play}
            style={[styles.container, { backgroundColor: colors.primary }]}
          >
            <IconPlayerButton colors={color} size={fontSize} iconName="play" />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={togglePlay}
            style={[styles.container, { backgroundColor: colors.primary }]}
          >
            <IconPlayerButton colors={color} size={fontSize} iconName="undo" />
          </TouchableOpacity>
        </>
      )}
    </View>
  );
}
